import React, { useEffect, useMemo, useState } from 'react';
import c from 'classnames';

import { useTranslation } from '@lyearn/i18n';
import { useStorageContext } from '@lyearn/lygo-core';
import { Popper } from '@lyearn/ui';

import { LinkBar } from '../../components/LinkBar';
import { MarksBar } from '../../components/MarksBar';
import { getMarksAttrs, isMarkActive } from './commands';
import { marks, marksLocalizedContent } from './consts';
import { z1301 } from './styles';
import { MarkTypesEnum, SelectionMenuProps } from './types';
import {
  useAnchorEl,
  useOffset,
  useOnHrefChange,
  useOnLinkDelete,
  useOnLinkSave,
  useOnMarkChangeAction,
  usePopperOptions,
} from './useActionSelectionMenu';

export const SelectionMenu: React.FC<SelectionMenuProps> = (props) => {
  let { open, editorView: view, link, customMarkItems = [], isLocalizedContent } = props;
  let [inLink, setIsLink] = useState(false);
  let [href, setHref] = useState(link || '');
  const { storage } = useStorageContext();
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) {
      setIsLink(false);
      setHref('');
    }
    if (link) {
      setIsLink(true);
      setHref(link);
    }
  }, [link, open]);

  const anchorEl = useAnchorEl(open, view);
  const offset = useOffset(open, view);
  const popperOptions = usePopperOptions(offset);

  const onMarkChangeAction = useOnMarkChangeAction(storage, view, setIsLink);
  const onLinkSave = useOnLinkSave(view, href);
  const onLinkDelete = useOnLinkDelete(view);
  const onHrefChange = useOnHrefChange(view, setHref);

  // We want selection menu at the top of selection. So it will get anchor element
  // which will be the block/dom-node where selection would have happened. It will
  // also get the off set value in popperOptions which will set the menu at the
  // top of the selection.
  const allNodes = view?.state?.schema?.nodes;
  const allMarks = view?.state?.schema?.marks;

  const filteredMarks = useMemo(() => {
    const filteredMarks = (isLocalizedContent ? marksLocalizedContent(t) : marks(t))
      .filter((mark) => {
        return mark.name === MarkTypesEnum.Break || allMarks?.[mark.name] || allNodes?.[mark.name];
      })
      .filter((mark, index, array) => {
        if (index === 0 && mark.name === MarkTypesEnum.Break) return false;
        if (index === array.length - 1 && mark.name === MarkTypesEnum.Break) return false;
        if (array[index - 1]?.name === MarkTypesEnum.Break && mark.name === MarkTypesEnum.Break)
          return false;

        return true;
      });

    return filteredMarks;
  }, [allMarks, allNodes, t]);

  return (
    <Popper
      anchorEl={anchorEl}
      className={c('rounded-8 border border-border bg-bg-primary shadow-02', z1301)}
      open={open}
      placement={document.dir === 'ltr' ? 'top-start' : 'top-end'}
      popperOptions={popperOptions}>
      {inLink ? (
        <LinkBar
          editMode={!!link}
          href={href}
          setHref={onHrefChange}
          onDelete={onLinkDelete}
          onSave={onLinkSave}
        />
      ) : (
        <MarksBar
          getMarksAttrs={getMarksAttrs}
          isMarkActive={isMarkActive}
          marks={[...(isLocalizedContent ? [] : customMarkItems), ...filteredMarks]}
          view={view}
          onMarkChangeAction={onMarkChangeAction}
        />
      )}
    </Popper>
  );
};
